import React, { useEffect, useState, useRef } from "react";

import InputComponent from "./components/InputComponent";
import CheckboxGroup from "./components/CheckboxComponent";
import RadioComponent from "./components/RadioComponent";
import SwitchComponent from "./components/SwitchComponent";
import Dropdown from "./components/DropDownComponent";
import FilterTitleComponent from "./components/FilterTitleComponent";

import ButtonComponent from "@components/ButtonComponent";
import clsx from "clsx";

import KeyboardEventHandler from "react-keyboard-event-handler";

import { useTranslation } from "react-i18next";
import { helperTitle, FormatWithCommas } from "@helpers/helpers";
import { toast } from "react-toastify";

import { ZERO_VALUE, ONE_VALUE } from "@utils/constants";
import BadgeComponent from "@components/BadgeComponent";
import {
  FIRST_INDEX,
  FOCUS_TIMEOUT_VALUE,
  DECIMAL_VALUE,
  MIN_HEIGHT,
  TOAST_TIMEOUT,
  DEBOUNCE_TIMEOUT,
  TAB_INDEX,
  RESET_INDEX,
  ADDITIONAL_FILTERS_INDEX,
  MAIN_FILTERS_INDEX,
  APPLY_BUTTON_INDEX,
  RESET_FILTER_INDEX,
  MAX_DATEPART_LENGTH,
} from "./constants";
import { X, FilterIcon, ArrowRight } from "lucide-react";

const FiltersComponent = (props) => {
  const {
    updateAppliedFilters,
    appliedFilters: parentAppliedFilters,
    openFilter,
    setOpenFilter,
    filterDisplayValues,
    filters,
    selectedFiltersDisplay,
    setSelectedValues,
    selectedValues,
    multipleValueColumns,
    extraParentClass,
    isScrollable = true,
    prefix,
    minHeight = MIN_HEIGHT,
  } = props;

  const { t: translate, i18n } = useTranslation();
  const debounceTimeout = useRef(null);

  useEffect(() => {
    if (
      parentAppliedFilters &&
      JSON.stringify(parentAppliedFilters) !== JSON.stringify(appliedFilters)
    ) {
      setSelectedValues(parentAppliedFilters);
      setAppliedfilters(parentAppliedFilters);
    }
  }, [parentAppliedFilters]);

  useEffect(() => {
    if (openFilter) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [openFilter]);

  const [selectedFilter, setSelectedFilter] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);
  const [clearFilterTriggered, setClearFilterTriggered] = useState(false);
  const [appliedFilters, setAppliedfilters] = useState({});
  const [validRange, setValidRange] = useState(true);
  const [toastID, setToastID] = useState(null);
  const [validStartEndDate, setValidStartEndDate] = useState(true);

  const mainButtonRef = useRef(null);
  const firstFocusableItem = useRef(null);
  useEffect(() => {
    mainButtonRef.current.focus();
  }, []);

  const getValuesArray = (key) => {
    return filterDisplayValues[key];
  };

  const handleSwitchChange = (e, filter) => {
    const { checked } = e.target;
    if (checked) {
      setSelectedValues((prevValues) => ({
        ...prevValues,
        [filter.key]: {
          value: true,
          filterType: filter.filterType,
          operator: filter.operator,
          displayName: filter.value,
        },
      }));
    } else {
      const { [filter.key]: removedValue, ...rest } = selectedValues;
      setSelectedValues(rest);
    }
  };

  const handleChange = (
    e,
    filter,
    isNumber = false,
    decimalPoint = DECIMAL_VALUE,
    isRange = false,
    valueToShow
  ) => {
    let { name, value } = e.target;
    if (value === "") {
      const { [name]: removedValue, ...rest } = selectedValues;
      setSelectedValues(rest);
    } else {
      let calculatedValue = isNumber
        ? !!decimalPoint
          ? parseFloat(value).toFixed(decimalPoint)
          : parseFloat(value).toString()
        : value;
      if (calculatedValue.includes(".")) {
        const parts = calculatedValue.split(".");
        if (parts[ONE_VALUE].length > DECIMAL_VALUE) {
          calculatedValue = `${parts[ZERO_VALUE]}.${parts[ONE_VALUE].substring(
            ZERO_VALUE,
            DECIMAL_VALUE
          )}`;
        }
      }

      if (
        (filter.dynamicMinDate &&
          filter.dynamicMinDate(selectedValues) !== undefined) ||
        (filter.dynamicMaxDate &&
          filter.dynamicMaxDate(selectedValues) !== undefined)
      ) {
        const minDate = filter.dynamicMinDate
          ? filter.dynamicMinDate(selectedValues)
          : null;
        const maxDate = filter.dynamicMaxDate
          ? filter.dynamicMaxDate(selectedValues)
          : null;
        const inputValue = e.target.value;

        if (
          (minDate && inputValue < minDate) ||
          (maxDate && inputValue > maxDate)
        ) {
          setToastID(toast.error(translate("shared.date-error")));
          setValidStartEndDate(false);
        } else {
          setValidStartEndDate(true);
        }
      }

      if (filter.inputType === "date") {
        const today = new Date();
        const todayString = today.toISOString().split("T")[ZERO_VALUE];

        if (value > todayString) {
          setValidStartEndDate(false);
          setTimeout(() => {
            setToastID(toast.error(translate("shared.invalid-date")));
          }, TOAST_TIMEOUT);
          return;
        } else {
          setValidStartEndDate(true);
          toast.update(toastID, {
            render: translate("shared.valid-date"),
            type: "success",
            isLoading: false,
            autoClose: 3000,
          });
        }
        const dateParts = value.split("-");

        if (dateParts[ZERO_VALUE].length > MAX_DATEPART_LENGTH) {
          dateParts[FIRST_INDEX] = dateParts[FIRST_INDEX].slice(
            FIRST_INDEX,
            MAX_DATEPART_LENGTH
          );
          value = dateParts.join("-");
        }
      }

      if (isRange) {
        if (debounceTimeout.current) {
          clearTimeout(debounceTimeout.current);
        }
        const isRangeValid =
          (filter.inputType === "number" &&
            parseFloat(valueToShow?.split("|")[ZERO_VALUE]) <=
              parseFloat(value)) ||
          (filter.inputType === "date" &&
            new Date(valueToShow?.split("|")[ZERO_VALUE]) <= new Date(value));

        debounceTimeout.current = setTimeout(() => {
          if (!isRangeValid) {
            setToastID(toast.error(translate("shared.range-error")));
            setValidRange(false);
          } else {
            toast.update(toastID, {
              render: translate("shared.range-success"),
              type: "success",
              isLoading: false,
              autoClose: 3000,
            });
            setValidRange(true);
          }
        }, DEBOUNCE_TIMEOUT);
        if (valueToShow) {
          calculatedValue =
            valueToShow.split("|")[ZERO_VALUE] + "|" + calculatedValue;
        } else {
          calculatedValue = value + "|";
        }
      }

      if (
        calculatedValue < ZERO_VALUE ||
        parseFloat(value).toFixed(decimalPoint) < ZERO_VALUE ||
        Object.is(parseFloat(value), ZERO_VALUE)
      ) {
        if (debounceTimeout.current) {
          clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
          setToastID(toast.error(translate("shared.negative-value-error")));
        }, DEBOUNCE_TIMEOUT);
      } else {
        setSelectedValues((prevValues) => ({
          ...prevValues,
          [name]: {
            value: calculatedValue,
            filterType: filter.filterType,
            operator: filter.operator,
            displayName:
              filter.filterType === "dropdown"
                ? filter?.values.find((f) => f.value === calculatedValue)
                    ?.displayName
                : calculatedValue,
          },
        }));
      }
    }
  };

  const handleSelectAll = (children, group) => {
    setSelectedValues((prev) => {
      let newState = { ...prev };
      const existingValues = newState[group.key]
        ? newState[group.key].value.split(",")
        : [];
      const existingDisplay = newState[group.key]
        ? newState[group.key].displayName.split(",")
        : [];

      children.forEach((childInner) => {
        if (!existingValues.includes(childInner.value)) {
          existingValues.push(childInner.value);
          existingDisplay.push(childInner.displayName);
        }
      });

      newState[group.key] = {
        value: existingValues.join(","),
        filterType: group.filterType,
        operator: group.operator,
        displayName: translate("shared.all"),
      };

      return newState;
    });
  };
  const handleDeselectAll = (children, group) => {
    setSelectedValues((prev) => {
      let newState = { ...prev };

      delete newState[group.key];

      return newState;
    });
  };

  const handleCheckBoxChange = (e, singleGroup, isChild = false, group) => {
    const { checked } = e.target;
    setSelectedValues((prev) => {
      let newState = { ...prev };

      if (checked) {
        if (!isChild) {
          // If it's a parent, add all its children to the array
          if (singleGroup.children) {
            const childValues = singleGroup.children.map(
              (child) => child.value
            );
            const childDisplay = singleGroup.children.map(
              (child) => child.displayName
            );

            const valuesJoined = childValues
              .map((childInner) => childInner)
              .join(",");
            const displayNamesJoined = childDisplay
              .map((childInner) => childInner)
              .join(",");

            newState[group.key] = {
              value: valuesJoined,
              filterType: group.filterType,
              operator: group.operator,
              displayName: displayNamesJoined,
            };
          } else {
            const singleValue = {
              value: singleGroup.value,
              filterType: group.filterType,
              operator: group.operator,
            };
            newState[group.key] = prev[group.key]
              ? [...new Set([...prev[group.key], singleValue])]
              : [singleValue];
          }
        } else if (isChild) {
          const parent = group.values.find((p) =>
            p.children
              ? p.children.some((child) => child.value === singleGroup.value)
              : p.value === singleGroup.value
          );
          if (parent) {
            const existing = newState[group.key]
              ? newState[group.key].value
              : "";
            const existingDisplay = newState[group.key]
              ? newState[group.key].displayName
              : "";
            newState[group.key] = {
              value: existing
                ? `${existing},${singleGroup.value}`
                : singleGroup.value,
              filterType: group.filterType,
              operator: group.operator,
              displayName: existingDisplay
                ? `${existingDisplay},${singleGroup.displayName}`
                : singleGroup.displayName,
            };
            // Check if all children are selected
            const allChildrenSelected = parent.children.every((child) =>
              newState[group.key].value.split(",").includes(child.value)
            );
            if (allChildrenSelected) {
              newState[group.key].displayName = translate("shared.all");
            }
          }
        }
      } else {
        if (!isChild) {
          if (singleGroup.children) {
            // If it's a parent with children, remove all its children from the state
            newState[group.key] = {
              value: singleGroup.children.forEach((child) => {
                newState[group.key].value
                  .split(",")
                  .filter((value) => value !== child.value);
              }),
              filterType: group.filterType,
              operator: group.operator,
              displayName: singleGroup.children.forEach((child) => {
                newState[group.key].displayName
                  .split(",")
                  .filter((displayName) => displayName !== child.displayName);
              }),
            };
          }
          if (
            newState[group.key].value === "" ||
            newState[group.key].value === undefined
          ) {
            delete newState[group.key];
          }
        } else if (isChild) {
          const parent = group.values.find((p) =>
            p.children
              ? p.children.some((child) => child.value === singleGroup.value)
              : p.value === singleGroup.value
          );
          // If it's a child, remove it from the parent's key
          newState[group.key] = {
            value: newState[group.key].value
              .split(",")
              .filter((status) => status !== singleGroup.value)
              .join(","),
            filterType: group.filterType,
            operator: group.operator,
            displayName: newState[group.key].displayName
              .split(",")
              .filter((status) => status !== singleGroup.displayName)
              .join(","),
          };

          // Check if all children are selected
          const allChildrenSelected = parent.children.every((child) =>
            newState[group.key].value.split(",").includes(child.value)
          );

          // If not all children are selected, update the display name to show selected values
          if (!allChildrenSelected) {
            const selectedChildren = parent.children.filter((child) =>
              newState[group.key].value.split(",").includes(child.value)
            );
            newState[group.key].displayName = selectedChildren
              .map((child) => child.displayName)
              .join(",");
          }

          // If all children are unchecked, remove the parent key if it exists
          if (parent) {
            if (newState[group.key].value === "") {
              delete newState[group.key];
            }
          }
        }
      }

      // If the array under the key is empty, remove the key from the newState
      if (newState[group.key] && newState[group.key].length === ZERO_VALUE) {
        delete newState[group.key];
      }

      return newState;
    });
  };
  const findValues = (key, valuesToFind, displayValue) => {
    let foundValues = [];
    let values = [];
    values = getValuesArray(key);

    values.forEach((method) => {
      if (method.children) {
        method.children.forEach((child) => {
          if (valuesToFind.includes(child.value)) {
            foundValues.push(child.displayName);
          }
        });
      } else {
        if (valuesToFind.includes(method.value)) {
          foundValues.push(method.displayName);
        }
      }
    });
    foundValues.length === ZERO_VALUE && foundValues.push(displayValue);
    return foundValues.join(", ");
  };

  const clearSingleFilter = (key, value) => {
    const newFilters = {
      ...selectedValues,
    };
    delete newFilters[key];
    setSelectedValues(newFilters);
    setAppliedfilters(newFilters);
    updateAppliedFilters(newFilters);
    setClearFilterTriggered(true);
    setSelectedFilter(null);
  };

  const handleResetFilters = () => {
    setSelectedValues({});
    setAppliedfilters({});
    updateAppliedFilters({});
    setSelectedFilter(null);
  };

  useEffect(() => {
    if (clearFilterTriggered) {
      setAppliedfilters(selectedValues);
      setClearFilterTriggered(false);
    }
  }, [clearFilterTriggered, selectedValues]);

  useEffect(() => {
    if (openFilter) {
      setTimeout(() => {
        // mainButtonRef.current.focus();
        firstFocusableItem?.current?.focus();
      }, FOCUS_TIMEOUT_VALUE);
    } else {
      mainButtonRef?.current?.focus();
    }
  }, [openFilter]);

  const renderActiveFilters = (selectedValues) => {
    return (
      <div
        data-testid="active-filters-container"
        className="relative z-[8] flex flex-wrap gap-3 items-center"
      >
        {Object.entries(selectedValues).map(([key, singleFilter], index) => {
          let displayKey = selectedFiltersDisplay[key] || key;

          let switchDisplayOrder = false;

          let displayValue = singleFilter?.displayName?.toString();
          if (typeof value === "boolean") {
            displayValue = singleFilter.value ? "" : "Not";
            displayKey = selectedFiltersDisplay[key];
            switchDisplayOrder = true;
          }
          let foundValuesString = multipleValueColumns.includes(key)
            ? findValues(key, singleFilter, displayValue)
            : displayValue?.split(",")?.join(", ");

          const isAmount = singleFilter.filterType === "amount";

          if (
            singleFilter.filterType === "date" ||
            singleFilter.filterType === "amount"
          ) {
            singleFilter.value.split("|").length > ONE_VALUE
              ? (foundValuesString =
                  singleFilter.value.split("|")[ZERO_VALUE] ===
                  singleFilter.value.split("|")[ONE_VALUE] ? (
                    isAmount ? (
                      FormatWithCommas(
                        parseFloat(singleFilter.value.split("|")[ZERO_VALUE])
                      )
                    ) : (
                      singleFilter.value.split("|")[ZERO_VALUE]
                    )
                  ) : (
                    <div className="flex items-center gap-1">
                      {isAmount
                        ? FormatWithCommas(
                            parseFloat(
                              singleFilter.value.split("|")[ZERO_VALUE]
                            )
                          )
                        : singleFilter.value.split("|")[ZERO_VALUE]}
                      <ArrowRight size={18} />
                      {isAmount
                        ? FormatWithCommas(
                            parseFloat(singleFilter.value.split("|")[ONE_VALUE])
                          )
                        : singleFilter.value.split("|")[ONE_VALUE]}
                    </div>
                  ))
              : (foundValuesString = isAmount
                  ? FormatWithCommas(parseFloat(singleFilter.value))
                  : singleFilter.value);
          }
          return (
            <BadgeComponent
              key={index}
              text={
                <div className="flex items-center gap-1 single-selected-filter rounded-lg group text-sm">
                  <div
                    data-testid="filter-value"
                    className={clsx(
                      `flex gap-1`,
                      switchDisplayOrder ? "flex-row-reverse" : "flex-row"
                    )}
                  >
                    <strong className="max-w-max w-full">{displayKey}</strong>
                    {foundValuesString}
                  </div>
                  <X
                    className="size-5 max-w-max w-full text-gray-400 cursor-pointer group-hover:text-red-500 hover:scale-125 hover:animate-pulse transition-all duration-700 ease-in-out"
                    onClick={() => clearSingleFilter(key, singleFilter)}
                  />
                </div>
              }
              color="primary"
            />
          );
        })}
        {Object.keys(appliedFilters).length >= RESET_FILTER_INDEX && (
          <ButtonComponent
            data-testid="reset-filters-button"
            withoutborder={true}
            id="reset-filters"
            color="red"
            type="cancel"
            onClick={handleResetFilters}
            ref={null}
            icon={null}
            text={translate("shared.reset-filters")}
            minwidth={false}
            className="self-end"
            size="sm"
            width="max"
            noTextSwap={true}
          />
        )}
      </div>
    );
  };

  const renderFilterValues = (filter, index, isTopFilter = false) => {
    return (
      <div
        data-testid="selected-filter-values"
        key={filter.key}
        className="flex flex-col gap-2"
        tabIndex={1}
      >
        {filter.components.map((singleComponent) => {
          if (singleComponent.type === "radioGroup") {
            return (
              <RadioComponent
                key={singleComponent.key}
                filter={singleComponent}
                handleChange={handleChange}
                value={selectedValues}
                index={index}
                ref={isTopFilter ? firstFocusableItem : null}
              />
            );
          } else if (
            singleComponent.type === "numberInput" ||
            singleComponent.type === "numberMin" ||
            singleComponent.type === "numberMax" ||
            singleComponent.type === "dateInput" ||
            singleComponent.type === "searchInput"
          ) {
            return (
              <InputComponent
                key={singleComponent.key}
                filter={singleComponent}
                handleChange={handleChange}
                value={selectedValues[singleComponent.key]}
                isNumber={
                  singleComponent.type === "numberInput" &&
                  singleComponent.filterType !== "amount"
                    ? true
                    : false
                }
                isText={singleComponent.type === "searchInput" ? true : false}
                index={index}
                ref={isTopFilter ? firstFocusableItem : null}
                clearRange={() => {
                  setValidRange(true);
                  toastID && toast.dismiss(toastID);
                }}
                selectedValues={selectedValues}
              />
            );
          } else if (singleComponent.type === "checkboxGroup") {
            return (
              <CheckboxGroup
                key={singleComponent.key}
                group={singleComponent}
                handleChange={handleCheckBoxChange}
                value={selectedValues}
                handleSelectAll={handleSelectAll}
                handleDeselectAll={handleDeselectAll}
                index={index}
                ref={isTopFilter ? firstFocusableItem : null}
              />
            );
          } else if (singleComponent.type === "switch") {
            return (
              <SwitchComponent
                key={singleComponent.key}
                filter={singleComponent}
                handleChange={handleSwitchChange}
                value={selectedValues}
                index={index}
                ref={isTopFilter ? firstFocusableItem : null}
              />
            );
          } else if (singleComponent.type === "dropDown") {
            return (
              <Dropdown
                key={singleComponent.key}
                filter={singleComponent}
                handleChange={handleChange}
                selectedOption={selectedValues[singleComponent.key]}
                index={index}
                ref={isTopFilter ? firstFocusableItem : null}
              />
            );
          } else if (singleComponent.type === "title") {
            return (
              <FilterTitleComponent key={index} filter={singleComponent} />
            );
          }
          return null; // Explicitly return null for components that don't match the condition
        })}
      </div>
    );
  };

  const handleClickOutside = () => {
    if (!validStartEndDate) {
      setSelectedValues(appliedFilters);
      setValidStartEndDate(true);
    } else if (
      JSON.stringify(selectedValues) !== JSON.stringify(appliedFilters)
    ) {
      setOpenPopup(true);
    } else {
      setTimeout(() => {
        mainButtonRef.current.focus();
      }, FOCUS_TIMEOUT_VALUE);
    }
    setOpenFilter(false);
  };

  const handleApplyFilters = () => {
    setOpenPopup(false);
    setOpenFilter(false);
    setAppliedfilters(selectedValues);
    updateAppliedFilters(selectedValues);
  };

  const handleApplyFiltersOld = () => {
    setSelectedValues(appliedFilters);
    setOpenPopup(false);
  };

  const handleButtonClick = () => {
    let TOP_OFFSET = 0;
    let LEFT_OFFSET = 0;
    setTimeout(() => {
      isScrollable &&
        document
          .getElementById("filters-options-menu")
          .scrollIntoView({ block: "start" });
      const parent = document.getElementById(`${prefix}_parent_filter`);
      const child = document.getElementById(`${prefix}_child`);

      if (child) {
        const parentRect = parent.getBoundingClientRect();

        const top = parentRect.top;
        const left = parentRect.left;
        if (i18n.language === "ar") {
          child.style.left = `unset`;
        } else {
          child.style.left = `${left + LEFT_OFFSET}px`;
        }
        child.style.top = `${top + TOP_OFFSET}px`;
      }
    }, ZERO_VALUE);

    openFilter ? handleClickOutside() : setOpenFilter(true);
  };

  const onKeyEvent = (key) => {
    if (key === "esc") {
      handleClickOutside(true);
    }
    if (key === "y") {
      handleApplyFilters();
    }
    if (key === "n") {
      handleApplyFiltersOld();
    }
    if (key === "r") {
      Object.keys(appliedFilters).length >= RESET_FILTER_INDEX &&
        handleResetFilters();
    }
  };
  const topFiltersLength =
    filters[FIRST_INDEX]?.topFilters?.length || ZERO_VALUE;
  const mainFiltersLength = filters[FIRST_INDEX].mainFilters.length;
  const additionalFiltersLength =
    filters[FIRST_INDEX]?.additionalFilters?.length || ZERO_VALUE;

  const filterdFoundInMainFilters = filters[FIRST_INDEX].mainFilters.find(
    (filter) => filter.key === selectedFilter
  );

  return (
    <KeyboardEventHandler
      handleKeys={["esc", "y", "n", "r"]}
      onKeyEvent={(key) => onKeyEvent(key)}
    >
      {openFilter && (
        <div
          className="fixed top-0 left-0 w-full h-dvh bg-black opacity-25 z-[8]"
          onClick={handleClickOutside}
        />
      )}
      <div
        id={`${prefix}_parent_filter`}
        className={clsx(
          `relative inline-flex gap-5 text-left text-black flex-col items-start`,
          extraParentClass,
          openFilter ? "z-[9]" : "z-[2]"
        )}
      >
        <div className="flex gap-5 items-start justify-start">
          <ButtonComponent
            data-testid="filters-button"
            isactive={openFilter}
            color="blue"
            id="filters-options-menu"
            onClick={() => handleButtonClick()}
            ref={mainButtonRef}
            // minwidth={false}
            width="max"
            noTextSwap={true}
            icon={<FilterIcon size={16} />}
            text={
              <span className="font-semibold">
                {helperTitle(translate("shared.columns-filters"))}
                {appliedFilters && !!Object.keys(appliedFilters).length && (
                  <span className="text-primary-400 group-hover:text-white text-xs font-normal ml-1">
                    {"("}
                    {Object.keys(appliedFilters).length}
                    {")"}
                  </span>
                )}
              </span>
            }
          />
          {renderActiveFilters(selectedValues)}
        </div>

        {openFilter && (
          <div
            id={`${prefix}_child`}
            className=" z-[9] fixed mt-10 w-max rounded-md shadow-lg bg-white dark:bg-gray-700 dark:text-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-100"
          >
            <div
              data-testid="filters-popup"
              className={clsx(
                "flex flex-row divide-gray-100",
                `min-h-[${minHeight}px]`
              )}
            >
              <div className="flex flex-col justify-between p-2 dark:bg-gray-800 bg-gray-50 w-[260px]">
                <div>
                  {!!filters[FIRST_INDEX]?.topFilters && (
                    <div className="border-b-gray-100 border-b-2 mb-4">
                      {filters[FIRST_INDEX]?.topFilters?.map((filter, index) =>
                        renderFilterValues(filter, index, true)
                      )}
                    </div>
                  )}
                  {filters[FIRST_INDEX].mainFilters.map((filter, index) => (
                    <button
                      data-testid={filter.key}
                      key={filter.key}
                      className={clsx(
                        `flex items-center gap-2 cursor-pointer mb-1 px-2 py-2 w-auto dark:hover:bg-gray-500 hover:bg-gray-100 hover:rounded-xl`,
                        selectedFilter === filter.key &&
                          "dark:bg-gray-600 bg-gray-200 rounded-xl"
                      )}
                      onClick={() => setSelectedFilter(filter.key)}
                      ref={
                        !filters[FIRST_INDEX]?.topFilters
                          ? !index
                            ? firstFocusableItem
                            : null
                          : null
                      }
                      tabIndex={topFiltersLength + index + ONE_VALUE}
                    >
                      {filter.icon && (
                        <div className="w-4 h-4">{filter.icon()}</div>
                      )}
                      {filter.displayName}
                    </button>
                  ))}
                  {showAdditionalFilters &&
                    filters[FIRST_INDEX].additionalFilters.map(
                      (filter, index) => (
                        <button
                          key={filter.key}
                          className={clsx(
                            `flex items-center gap-2 cursor-pointer px-2 py-2 w-auto hover:bg-gray-100 hover:rounded-xl`,
                            selectedFilter === filter.key &&
                              "bg-gray-200 rounded-xl"
                          )}
                          onClick={() => setSelectedFilter(filter.key)}
                          tabIndex={
                            topFiltersLength +
                            mainFiltersLength +
                            additionalFiltersLength +
                            index +
                            RESET_INDEX
                          }
                        >
                          {filter.icon && (
                            <div className="w-4 h-4">{filter.icon()}</div>
                          )}
                          {filter.displayName}
                        </button>
                      )
                    )}
                  {!!additionalFiltersLength && (
                    <button
                      onClick={() => {
                        setShowAdditionalFilters(!showAdditionalFilters);
                      }}
                      className="text-xs px-2 py-2 hover:underline text-gray-400"
                      tabIndex={
                        showAdditionalFilters
                          ? topFiltersLength +
                            mainFiltersLength +
                            additionalFiltersLength +
                            MAIN_FILTERS_INDEX
                          : topFiltersLength +
                            mainFiltersLength +
                            MAIN_FILTERS_INDEX
                      }
                    >
                      {showAdditionalFilters
                        ? translate("shared.hide-additional-filters")
                        : translate("shared.show-additional-filters")}
                    </button>
                  )}
                </div>
                <ButtonComponent
                  data-testid="apply-filters-button"
                  color="green"
                  id="apply-filters"
                  onClick={handleApplyFilters}
                  ref={null}
                  icon={null}
                  text={translate("shared.apply-filters")}
                  {...((JSON.stringify(selectedValues) ===
                    JSON.stringify(appliedFilters) ||
                    !validRange ||
                    !validStartEndDate) && {
                    disabled: true,
                  })}
                  tabIndex={
                    topFiltersLength +
                    mainFiltersLength +
                    additionalFiltersLength +
                    ADDITIONAL_FILTERS_INDEX
                  }
                />
              </div>
              <div className="p-4 pr-10 pl-8 min-w-[400px] rtl:text-right">
                {selectedFilter ? (
                  <div>
                    {renderFilterValues(
                      filterdFoundInMainFilters ||
                        filters[FIRST_INDEX].additionalFilters.find(
                          (filter) => filter.key === selectedFilter
                        ),
                      filterdFoundInMainFilters
                        ? topFiltersLength +
                            MAIN_FILTERS_INDEX +
                            filters[FIRST_INDEX].mainFilters.findIndex(
                              (filter) => filter.key === selectedFilter
                            )
                        : topFiltersLength +
                            mainFiltersLength +
                            additionalFiltersLength +
                            APPLY_BUTTON_INDEX +
                            filters[FIRST_INDEX].additionalFilters.findIndex(
                              (filter) => filter.key === selectedFilter
                            )
                    )}
                  </div>
                ) : (
                  <div>{translate("shared.select-filter-message")}</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {openPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-[100]">
          <div className="bg-white dark:bg-gray-700 dark:text-white rounded-lg shadow-lg p-6 relative min-w-[400px]">
            <p className="text-center">
              {translate("shared.filters-confirmation-message")}
            </p>
            <div className="flex text-center w-full justify-around items-center gap-2 mt-6">
              <ButtonComponent
                color="greenConfirmation"
                id="accept"
                onClick={handleApplyFilters}
                ref={null}
                icon={null}
                text={translate("shared.yes")}
                width="1/2"
              />
              <ButtonComponent
                color="red"
                id="cancel"
                type="cancel"
                onClick={handleApplyFiltersOld}
                ref={null}
                icon={null}
                text={translate("shared.no")}
                width="1/2"
              />
            </div>
          </div>
        </div>
      )}
    </KeyboardEventHandler>
  );
};

export default FiltersComponent;
